import React, { useCallback, useEffect, useState } from 'react';
import { getScheduledJobs, ScheduledJobState, ScheduledJobStatus } from '../api.scheduledJobs';
import SectionLoader from '../components/SectionLoader';

const getToken = () => localStorage.getItem('token') || '';

const AUTO_REFRESH_MS = 30_000;

const STATE_META: Record<ScheduledJobState, { label: string; color: string; dot: string }> = {
  SUCCEEDED: { label: 'Succeeded', color: '#276749', dot: '#38a169' },
  FAILED: { label: 'Failed', color: '#9b2c2c', dot: '#e53e3e' },
  RUNNING: { label: 'Running', color: '#2c5282', dot: '#3182ce' },
  NEVER_RUN: { label: 'Never run', color: '#718096', dot: '#cbd5e0' },
};

const formatWhen = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—');

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const StatePill: React.FC<{ state: ScheduledJobState }> = ({ state }) => {
  const meta = STATE_META[state];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600, color: meta.color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot, flexShrink: 0 }} />
      {meta.label}
    </span>
  );
};

const AdminScheduledJobs: React.FC = () => {
  const [jobs, setJobs] = useState<ScheduledJobStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const fetchJobs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getScheduledJobs(getToken());
      setJobs(data);
      setLastFetchedAt(new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load scheduled jobs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const id = window.setInterval(() => fetchJobs(true), AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchJobs]);

  const failedCount = jobs.filter(j => j.state === 'FAILED').length;
  const neverRunCount = jobs.filter(j => j.state === 'NEVER_RUN').length;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>Scheduled Jobs</h1>
          <p style={{ color: '#718096', marginBottom: 0, fontSize: '0.9rem' }}>
            Every background job in the app — what runs, when, and whether it's healthy.
          </p>
        </div>
        <button
          onClick={() => fetchJobs()}
          disabled={loading}
          style={{
            padding: '7px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff',
            color: '#2563eb', fontWeight: 600, fontSize: '0.85rem', cursor: loading ? 'default' : 'pointer',
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {lastFetchedAt && (
        <div style={{ color: '#a0aec0', fontSize: '0.76rem', marginBottom: 20 }}>
          Last updated {lastFetchedAt.toLocaleTimeString()} · refreshes automatically every 30s
        </div>
      )}

      {error && (
        <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8, marginBottom: 16 }}>{error}</div>
      )}

      {loading && jobs.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <SectionLoader label="Loading scheduled jobs…" minHeight={200} />
        </div>
      ) : jobs.length === 0 ? (
        !error && <div style={{ color: '#718096', padding: 24, textAlign: 'center' }}>No scheduled jobs registered yet.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <SummaryChip label="Total jobs" value={jobs.length} color="#2563eb" />
            <SummaryChip label="Failed" value={failedCount} color={failedCount > 0 ? '#e53e3e' : '#a0aec0'} />
            <SummaryChip label="Never run" value={neverRunCount} color={neverRunCount > 0 ? '#d97706' : '#a0aec0'} />
          </div>

          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f7fafc' }}>
                    <Th>Job</Th>
                    <Th>Schedule</Th>
                    <Th>Status</Th>
                    <Th>Last run</Th>
                    <Th>Duration</Th>
                    <Th>Runs</Th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem', color: '#1a202c', verticalAlign: 'top' }}>
                        {job.name}
                        {job.state === 'FAILED' && job.lastError && (
                          <div style={{ color: '#c53030', fontSize: '0.75rem', marginTop: 3, fontFamily: 'inherit', maxWidth: 360, whiteSpace: 'normal' }}>
                            {job.lastError}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#4a5568', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{job.schedule}</td>
                      <td style={{ padding: '10px 14px', verticalAlign: 'top', whiteSpace: 'nowrap' }}><StatePill state={job.state} /></td>
                      <td style={{ padding: '10px 14px', color: '#4a5568', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{formatWhen(job.lastStartedAt)}</td>
                      <td style={{ padding: '10px 14px', color: '#4a5568', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{formatDuration(job.lastDurationMs)}</td>
                      <td style={{ padding: '10px 14px', color: '#4a5568', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {job.runCount}{job.failureCount > 0 && <span style={{ color: '#e53e3e' }}> ({job.failureCount} failed)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{
    textAlign: 'left', padding: '10px 14px', fontSize: '0.7rem', fontWeight: 700,
    color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em',
  }}>
    {children}
  </th>
);

const SummaryChip: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', gap: 6, padding: '8px 14px',
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
  }}>
    <span style={{ fontSize: '1.1rem', fontWeight: 700, color }}>{value}</span>
    <span style={{ fontSize: '0.78rem', color: '#718096' }}>{label}</span>
  </div>
);

export default AdminScheduledJobs;
